/**
 * Scanner Mixin for Vue Components
 * 
 * This mixin provides easy integration of wireless scanner functionality
 * into Vue components with automatic setup and cleanup.
 */

import wirelessScannerService from '@/services/wireless-scanner-service';

export const ScannerMixin = {
    data() {
        return {
            scannerConnected: false,
            scannerActive: false,
            scannerStatus: 'Disconnected',
            lastScanData: '',
            scanHistory: []
        };
    },

    created() {
        // Initialize scanner data from service
        this.updateScannerState();
    },

    async mounted() {
        // Set up scanner listeners
        this.setupScannerListeners();
        
        // Auto-initialize scanner if needed
        if (this.autoInitializeScanner !== false) {
            await this.initializeScanner();
        }
    },

    beforeDestroy() {
        // Clean up scanner listeners
        this.cleanupScannerListeners();
    },

    methods: {
        /**
         * Initialize the wireless scanner
         */
        async initializeScanner() {
            try {
                await wirelessScannerService.initialize();
                this.updateScannerState();
                console.log('Scanner initialized from mixin');
            } catch (error) {
                console.error('Failed to initialize scanner from mixin:', error);
                this.$toast?.error?.('Failed to initialize scanner');
            }
        },

        /**
         * Start scanning
         */
        async startScanning() {
            try {
                await wirelessScannerService.startScanning();
                this.updateScannerState();
                console.log('Scanner started from mixin');
            } catch (error) {
                console.error('Failed to start scanner from mixin:', error);
                this.$toast?.error?.('Failed to start scanner');
            }
        },

        /**
         * Stop scanning
         */
        stopScanning() {
            try {
                wirelessScannerService.stopScanning();
                this.updateScannerState();
                console.log('Scanner stopped from mixin');
            } catch (error) {
                console.error('Failed to stop scanner from mixin:', error);
            }
        },

        /**
         * Toggle scanner on/off
         */
        async toggleScanner() {
            if (this.scannerActive) {
                this.stopScanning();
            } else {
                await this.startScanning();
            }
        },

        /**
         * Test scan functionality
         */
        testScan(value) {
            wirelessScannerService.testScan(value);
        },

        /**
         * Clear scan history
         */
        clearScanHistory() {
            wirelessScannerService.clearHistory();
            this.updateScannerState();
        },

        /**
         * Update scanner state from service
         */
        updateScannerState() {
            const statusInfo = wirelessScannerService.getStatusInfo();
            this.scannerConnected = statusInfo.connected;
            this.scannerActive = statusInfo.active;
            this.scannerStatus = statusInfo.status;
            this.lastScanData = statusInfo.lastScan;
            this.scanHistory = statusInfo.scanHistory;
        },

        /**
         * Set up scanner event listeners
         */
        setupScannerListeners() {
            // Subscribe to scan events
            this.onScanHandler = this.handleScanEvent.bind(this);
            wirelessScannerService.onScan(this.onScanHandler);

            // Subscribe to status events
            this.onStatusHandler = this.handleStatusEvent.bind(this);
            wirelessScannerService.onStatusChange(this.onStatusHandler);
        },

        /**
         * Clean up scanner event listeners
         */
        cleanupScannerListeners() {
            if (this.onScanHandler) {
                wirelessScannerService.offScan(this.onScanHandler);
            }
            if (this.onStatusHandler) {
                wirelessScannerService.offStatusChange(this.onStatusHandler);
            }
        },

        /**
         * Handle scan events - override in components
         */
        handleScanEvent(scanData) {
            console.log('Scan received in mixin:', scanData);
            this.updateScannerState();
            
            // Call component-specific handler if it exists
            if (typeof this.onScanReceived === 'function') {
                this.onScanReceived(scanData);
            }
        },

        /**
         * Handle status events
         */
        handleStatusEvent(status) {
            console.log('Scanner status changed:', status);
            this.updateScannerState();
            
            // Call component-specific handler if it exists
            if (typeof this.onScannerStatusChanged === 'function') {
                this.onScannerStatusChanged(status);
            }
        },

        /**
         * Check if scanned data is a product barcode
         */
        isProductBarcode(scanData) {
            // UPC/EAN patterns or custom product ID patterns
            const patterns = [
                /^\d{8,14}$/, // UPC/EAN
                /^PROD-/,     // Custom product prefix
                /^[A-Z0-9]{8,}$/ // General alphanumeric
            ];
            
            return patterns.some(pattern => pattern.test(scanData));
        },

        /**
         * Check if scanned data is an invoice ID
         */
        isInvoiceId(scanData) {
            const patterns = [
                /^INV-/,
                /^(INV|INVOICE)[_-]?\d+/i
            ];
            
            return patterns.some(pattern => pattern.test(scanData));
        },

        /**
         * Check if scanned data is a customer ID
         */
        isCustomerId(scanData) {
            const patterns = [
                /^CUST-/,
                /^(CUST|CUSTOMER)[_-]?\d+/i
            ];
            
            return patterns.some(pattern => pattern.test(scanData));
        },

        /**
         * Process scan data based on type
         */
        async processScanData(scanData) {
            try {
                if (this.isProductBarcode(scanData)) {
                    await this.handleProductScan(scanData);
                } else if (this.isInvoiceId(scanData)) {
                    await this.handleInvoiceScan(scanData);
                } else if (this.isCustomerId(scanData)) {
                    await this.handleCustomerScan(scanData);
                } else {
                    await this.handleGenericScan(scanData);
                }
            } catch (error) {
                console.error('Error processing scan data:', error);
                this.$toast?.error?.(`Error processing scan: ${error.message}`);
            }
        },

        /**
         * Handle product scan - override in components
         */
        async handleProductScan(scanData) {
            console.log('Product scanned:', scanData);
            // Override in components that need product scanning
        },

        /**
         * Handle invoice scan - override in components
         */
        async handleInvoiceScan(scanData) {
            console.log('Invoice scanned:', scanData);
            // Override in components that need invoice scanning
        },

        /**
         * Handle customer scan - override in components
         */
        async handleCustomerScan(scanData) {
            console.log('Customer scanned:', scanData);
            // Override in components that need customer scanning
        },

        /**
         * Handle generic scan - override in components
         */
        async handleGenericScan(scanData) {
            console.log('Generic scan:', scanData);
            // Override in components for custom handling
        }
    },

    computed: {
        scannerStatusColor() {
            if (!this.scannerConnected) return 'red';
            if (this.scannerActive) return 'green';
            return 'orange';
        },

        scannerStatusText() {
            if (!this.scannerConnected) return 'Disconnected';
            if (this.scannerActive) return 'Active';
            return 'Connected';
        }
    }
};

export default ScannerMixin; 